import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ResponsiveImage from 'components/ResponsiveImage';

import EmployeeType from 'types/EmployeeType';
import EmployeeModal from 'components/EmployeeModal';
import Link, { LINK_BEHAVIORS, LINK_SIZES, LINK_THEMES } from 'components/Link';
import LinkButton, { LINK_BUTTON_THEMES } from 'components/LinkButton';
import { breakpoints } from 'js/breakpoints';

const VIEWS = {
  EMPLOYEE: 'employee',
  MARKETGROUP: 'marketgroup',
  PRESSTOPCONTACT: 'presstopcontact',
};
import fallbackImageSrc from '../../assets/person-icon.svg';

import * as css from './EmployeeCard.scss';

class EmployeeCard extends React.PureComponent {
  static propTypes = {
    ...EmployeeType,
    view: PropTypes.oneOf([
      VIEWS.EMPLOYEE,
      VIEWS.MARKETGROUP,
      VIEWS.PRESSTOPCONTACT,
    ]),
    className: PropTypes.string,
    negative: PropTypes.bool.isRequired,
    customDownloadLink: PropTypes.exact(Link.propTypes),
    trackUrl: PropTypes.string,
  };

  static defaultProps = {
    negative: false,
    view: 'employee',
    isBoardMember: false,
  };

  constructor() {
    super();

    this.state = {
      activeEmployee: false,
    };

    this._onClick = this._onClick.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onUrlLinkClick = this._onUrlLinkClick.bind(this);
  }

  _onClick = e => {
    e.preventDefault();

    this.setState({
      activeEmployee: true,
    });
  };

  _onClose = () => this.setState({ activeEmployee: false });

  _onUrlLinkClick = e => {
    if (this.props.trackUrl) {
      var defaultBehaviour = e.ctrlKey;
      if (!defaultBehaviour) {
        e.preventDefault();
      }
      fetch(this.props.trackUrl).then(() => {
        if (!defaultBehaviour && this.props.url) {
          window.location.href = this.props.url;
        }
      });
    }
  };

  render() {
    const {
      id,
      email,
      phone,
      className,
      name,
      negative,
      image,
      title,
      url,
      view,
      isBoardMember,
      modalLabels,
      intro,
      customDownloadLink,
    } = this.props;

    const { activeEmployee } = this.state;
    const fallbackImage = {
      src: fallbackImageSrc,
    };

    return (
      <div
        className={cn('employee-card', css.card, className, {
          [css.employee]: view === VIEWS.EMPLOYEE || !view,
          [css.marketgroup]: view === VIEWS.MARKETGROUP,
          [css.negative]: negative,
          [css.boardMemberHeader]: isBoardMember,
          [css.pressTopContact]: view === VIEWS.PRESSTOPCONTACT,
        })}
      >
        {view === VIEWS.PRESSTOPCONTACT ? (
          <header className={css.header_pressTopContact}>
            {name && <p className={css.pressTopContactName}>{name}</p>}
            {title && <p className={css.pressTopContactTitle}>{title}</p>}
            {intro && (
              <div
                className={cn(css.intro_pressTopContact, 'wysiwyg')}
                dangerouslySetInnerHTML={{ __html: intro }}
              />
            )}
            <div className={css.pressTopContactButtons}>
              {modalLabels && modalLabels.readMore && (
                <LinkButton
                  className={css.readMoreLink_pressTopContact}
                  onClick={this._onClick}
                  additionalAttributes={{
                    'data-id': id,
                  }}
                  url="#"
                  theme={LINK_BUTTON_THEMES.dark}
                  text={modalLabels.readMore}
                />
              )}
              {customDownloadLink ? (
                <LinkButton
                  theme={LINK_BUTTON_THEMES.outlineDark}
                  {...customDownloadLink}
                  className={css.downloadImageLink_pressTopContact}
                />
              ) : (
                <LinkButton
                  theme={LINK_BUTTON_THEMES.outlineDark}
                  openInNewTab={true}
                  url={image?.src}
                  text={modalLabels.downloadImage}
                  className={css.downloadImageLink_pressTopContact}
                />
              )}
            </div>
          </header>
        ) : (
          <header className="typo-small">
            <p
              className={cn({
                h4: view === VIEWS.MARKETGROUP,
              })}
            >
              {url && view != VIEWS.MARKETGROUP && (
                <Link
                  additionalAttributes={{
                    'data-name': name,
                  }}
                  url={url}
                  size={LINK_SIZES.small}
                  behavior={LINK_BEHAVIORS.text}
                  onClick={this._onUrlLinkClick}
                  theme={negative ? LINK_THEMES.lightTotal : LINK_THEMES.dark}
                  text={name}
                />
              )}
              {!url && view != VIEWS.MARKETGROUP && name}
              {view === VIEWS.MARKETGROUP && name}
            </p>
            {title && <p className={css.contactTitle}>{title}</p>}
            {!isBoardMember && (
              <ul className={css.contactList}>
                {view === VIEWS.MARKETGROUP && phone && (
                  <li className={css.list}>
                    <Link
                      url={`tel:${phone}`}
                      theme={
                        negative ? LINK_THEMES.lightTotal : LINK_THEMES.dark
                      }
                      text={phone}
                      size={LINK_SIZES.small}
                      behavior={LINK_BEHAVIORS.text}
                    />
                  </li>
                )}
                {view === VIEWS.MARKETGROUP && email && (
                  <li className={css.list}>
                    <Link
                      url={`mailto:${email}`}
                      theme={
                        negative ? LINK_THEMES.lightTotal : LINK_THEMES.dark
                      }
                      text={email}
                      size={LINK_SIZES.small}
                      behavior={LINK_BEHAVIORS.text}
                    />
                  </li>
                )}
              </ul>
            )}
            {isBoardMember && modalLabels && modalLabels.readMore && (
              <Link
                className={css.readMoreLink}
                onClick={this._onClick}
                data-id={id}
                url="#"
                theme={negative ? LINK_THEMES.lightTotal : LINK_THEMES.dark}
                behavior={LINK_BEHAVIORS.text}
                size={LINK_SIZES.small}
                text={modalLabels.readMore}
              />
            )}
          </header>
        )}

        <div className={cn('image', css.image)}>
          <ResponsiveImage
            {...(image ?? fallbackImage)}
            loading="lazy"
            fluid
            sources={
              view === VIEWS.PRESSTOPCONTACT
                ? [
                    {
                      width: 375,
                      height: 233,
                      media: breakpoints.xs,
                    },
                    {
                      width: 712,
                      height: 442,
                      media: breakpoints.s,
                    },
                    {
                      width: 460,
                      height: 307,
                      media: breakpoints.m,
                    },
                    {
                      width: 660,
                      height: 467,
                      // Note: Image is scaling according to design up to the screen width 1440px
                      media: 1440,
                    },
                  ]
                : [
                    {
                      width: 120,
                      height: 120,
                      media: breakpoints.m,
                    },
                    {
                      width: 240,
                      height: 240,
                      media: breakpoints.l,
                    },
                  ]
            }
          />
        </div>
        {activeEmployee && (
          <EmployeeModal {...this.props} onClose={this._onClose} />
        )}
      </div>
    );
  }
}
export default EmployeeCard;
